import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { parseMetaMarkup } from '@fiodecobre/shared';
import { CATEGORIA_NENHUMA } from '../produtos/ProdutosLista';

// Metas de Margem (indices financeiros, Fase 1 — migration 0300).
// A regua e' o MARKUP sobre o custo, a mesma do "Mrg Pix" do cadastro. Meta padrao da loja + meta
// opcional por categoria (vazio = segue a padrao). Grava POR LINHA: nada nasce marcado nem aplicado
// em massa — cada meta e' um gesto dele.

interface LinhaMeta {
  id: number | null;
  nome: string;
  status: string;
  meta: number | null;
  meta_efetiva: number;
  produtos: number;
  sem_custo: number;
  abaixo: number;
  mediana_markup: number | null;
}
interface Resposta {
  padrao: number;
  categorias: LinhaMeta[];
  sem_categoria: LinhaMeta;
  loja: { produtos: number; sem_custo: number; abaixo: number; mediana_markup: number | null };
}

const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
const txtMeta = (n: number | null) => (n == null ? '' : n.toLocaleString('pt-BR', { maximumFractionDigits: 2 }));

export function MetasMargem() {
  const toast = useToast();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const podeEditar = usuario?.perfil === 'admin';
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [padraoTxt, setPadraoTxt] = useState('');
  // Rascunho por categoria (id -> texto digitado). So existe enquanto difere do gravado.
  const [rascunho, setRascunho] = useState<Record<number, string>>({});
  const [gravando, setGravando] = useState<number | 'padrao' | null>(null);
  const [busca, setBusca] = useState('');

  async function carregar() {
    try {
      const r = await api.get<Resposta>('/api/margens/metas');
      setDados(r);
      setPadraoTxt(txtMeta(r.padrao));
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Categorias inativas sem produto ativo nao tem o que medir — somem da lista.
  const linhas = useMemo(() => {
    if (!dados) return [];
    const q = busca.trim().toUpperCase();
    return dados.categorias
      .filter((c) => c.status === 'ativa' || c.produtos > 0)
      .filter((c) => !q || c.nome.toUpperCase().includes(q));
  }, [dados, busca]);

  const padraoNovo = parseMetaMarkup(padraoTxt);
  const padraoMudou = dados != null && padraoNovo !== undefined && padraoNovo !== null && padraoNovo !== dados.padrao;

  async function gravarPadrao() {
    if (padraoNovo === undefined || padraoNovo === null) return;
    setGravando('padrao');
    try {
      await api.put('/api/margens/meta-padrao', { meta: padraoNovo });
      toast.sucesso(`Meta padrao da loja: ${pct(padraoNovo)}`);
      await carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setGravando(null);
    }
  }

  async function gravarCategoria(l: LinhaMeta) {
    if (l.id == null) return;
    const novo = parseMetaMarkup(rascunho[l.id] ?? '');
    if (novo === undefined) return;
    setGravando(l.id);
    try {
      await api.put(`/api/margens/categorias/${l.id}`, { meta: novo });
      toast.sucesso(novo === null ? `${l.nome}: volta a seguir a meta padrao` : `${l.nome}: meta ${pct(novo)}`);
      setRascunho((r) => { const n = { ...r }; delete n[l.id!]; return n; });
      await carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setGravando(null);
    }
  }

  // Abre a lista de Produtos ja filtrada: aquela categoria + "abaixo da meta" (ou "sem custo").
  // A ProdutosLista le os filtros do sessionStorage na montagem.
  // `null` = "(sem categoria)" — vira o CATEGORIA_NENHUMA que o filtro da lista entende ([DA-761]).
  function abrirProdutos(categoriaId: number | null, margem: '' | 'abaixo_meta' | 'sem_custo') {
    categoriaId = categoriaId ?? CATEGORIA_NENHUMA;
    try {
      sessionStorage.setItem('produtos.filtros', JSON.stringify({ categoriaId: categoriaId ?? '', margem }));
    } catch {}
    navigate('/produtos');
  }

  const sticky = useStickyHeader();

  const numLink = (n: number, onClick: () => void, titulo: string) =>
    n > 0 ? (
      <button type="button" onClick={onClick} title={titulo}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', fontWeight: 700, color: 'inherit' }}>
        {n}
      </button>
    ) : <span className="muted">0</span>;

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Metas de Margem</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar categoria..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
        </div>

        {dados && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 10px', fontSize: 13 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <strong>Meta padrão da loja</strong>
              <input
                type="text" inputMode="decimal" value={padraoTxt} disabled={!podeEditar}
                onChange={(e) => setPadraoTxt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && padraoMudou) gravarPadrao(); }}
                className="cel-num" style={{ width: 70, textAlign: 'right', padding: '4px 6px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)' }}
              />%
              {padraoMudou && (
                <button type="button" className="btn-primario" disabled={gravando === 'padrao'} onClick={gravarPadrao}>Gravar</button>
              )}
              {padraoNovo === undefined && <span style={{ fontWeight: 700 }}>use um % de 0 a 1000</span>}
            </label>
            <span className="muted">
              Loja: {dados.loja.produtos} produtos ativos · <strong style={{ color: 'var(--text-color)' }}>{dados.loja.abaixo}</strong> abaixo da meta ·{' '}
              {dados.loja.sem_custo} sem custo · mediana {pct(dados.loja.mediana_markup)}
            </span>
          </div>
        )}
        <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          Margem aqui é o <strong>markup sobre o custo</strong> — a mesma conta do <em>Mrg Pix</em> do cadastro: (Pix − Custo) ÷ Custo.
          Categoria com a meta vazia segue a meta padrão da loja. Produto sem custo não entra na conta: aparece em "Sem custo".
        </p>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : !dados ? null : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead>
            <tr>
              <th style={sticky.thStyle}>Categoria</th>
              <th style={{ ...sticky.thStyle, textAlign: 'right', width: 90 }}>Produtos</th>
              <th style={{ ...sticky.thStyle, textAlign: 'right', width: 110 }}>Mediana hoje</th>
              <th style={{ ...sticky.thStyle, textAlign: 'right', width: 200 }}>Meta</th>
              <th style={{ ...sticky.thStyle, textAlign: 'right', width: 120 }}>Abaixo da meta</th>
              <th style={{ ...sticky.thStyle, textAlign: 'right', width: 90 }}>Sem custo</th>
            </tr>
          </thead>
          <tbody>
            {/* O maior balde (59% dos ativos em 26/09) vai no TOPO — no fim da lista ele ficava abaixo da dobra. */}
            {!busca.trim() && (
              <tr>
                <td className="muted">{dados.sem_categoria.nome}</td>
                <td className="cel-num" style={{ textAlign: 'right' }}>
                  {numLink(dados.sem_categoria.produtos, () => abrirProdutos(null, ''), 'Abrir os produtos sem categoria (pra classificar em lote)')}
                </td>
                <td className="cel-num" style={{ textAlign: 'right' }}>{pct(dados.sem_categoria.mediana_markup)}</td>
                <td className="muted" style={{ textAlign: 'right' }}>segue a padrão ({pct(dados.padrao)})</td>
                <td className="cel-num" style={{ textAlign: 'right' }}>
                  {numLink(dados.sem_categoria.abaixo, () => abrirProdutos(null, 'abaixo_meta'), 'Abrir os produtos sem categoria abaixo da meta padrao')}
                </td>
                <td className="cel-num" style={{ textAlign: 'right' }}>
                  {numLink(dados.sem_categoria.sem_custo, () => abrirProdutos(null, 'sem_custo'), 'Abrir os produtos sem categoria e sem custo')}
                </td>
              </tr>
            )}
            {linhas.map((l) => {
              const id = l.id!;
              const txt = rascunho[id] ?? txtMeta(l.meta);
              const novo = parseMetaMarkup(txt);
              const mudou = rascunho[id] !== undefined && novo !== undefined && novo !== l.meta;
              return (
                <tr key={id}>
                  <td>{l.nome}{l.status !== 'ativa' && <span className="muted"> (inativa)</span>}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {numLink(l.produtos, () => abrirProdutos(id, ''), `Abrir os ${l.produtos} produtos de ${l.nome}`)}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{pct(l.mediana_markup)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {mudou && (
                      <>
                        <button type="button" className="btn-primario" style={{ padding: '2px 10px', marginRight: 4 }}
                          disabled={gravando === id} onClick={() => gravarCategoria(l)}>Gravar</button>
                        <button type="button" className="btn-secundario" style={{ padding: '2px 8px', marginRight: 6 }}
                          onClick={() => setRascunho((r) => { const n = { ...r }; delete n[id]; return n; })}>Desfazer</button>
                      </>
                    )}
                    {novo === undefined && <span style={{ fontWeight: 700, marginRight: 6 }}>0 a 1000</span>}
                    <input
                      type="text" inputMode="decimal" value={txt} disabled={!podeEditar}
                      placeholder="padrão"
                      onChange={(e) => setRascunho((r) => ({ ...r, [id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter' && mudou) gravarCategoria(l); }}
                      className="cel-num"
                      style={{ width: 90, textAlign: 'right', padding: '2px 6px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)' }}
                    /> %
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {numLink(l.abaixo, () => abrirProdutos(id, 'abaixo_meta'), `Abrir os ${l.abaixo} produtos de ${l.nome} abaixo de ${pct(l.meta_efetiva)}`)}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {numLink(l.sem_custo, () => abrirProdutos(id, 'sem_custo'), `Abrir os ${l.sem_custo} produtos de ${l.nome} sem custo`)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
